import { Injectable } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * HttpContext token carrying the batchId for a single per-notebook request
 * inside a batch operation. When set, the BatchIdInterceptor injects an
 * `X-Batch-Id` header on the outgoing request.
 *
 * Treat the batchId as a HINT for audit grouping, never a trust signal —
 * the server should not cross-reference batchIds between users.
 */
export const BATCH_ID_CONTEXT = new HttpContextToken<string | null>(() => null);

/**
 * Convenience builder for callers:
 *
 *   this.http.post(url, body, { context: withBatchId(batchId) });
 */
export function withBatchId(batchId: string, base: HttpContext = new HttpContext()): HttpContext {
  return base.set(BATCH_ID_CONTEXT, batchId);
}

/**
 * Injects `X-Batch-Id: <uuid>` only when the request was tagged via
 * `BATCH_ID_CONTEXT`. Non-batch requests pass through unchanged so this
 * cannot leak the header onto unrelated calls (e.g., MSAL token refresh).
 *
 * Format validation is intentionally minimal here — the server is the
 * authoritative validator (per BACKEND-COORDINATION.md §4). We just reject
 * obviously bogus values (non-string, empty, whitespace).
 */
@Injectable()
export class BatchIdInterceptor implements HttpInterceptor {
  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const batchId = req.context.get(BATCH_ID_CONTEXT);
    if (!batchId || typeof batchId !== 'string' || batchId.trim().length === 0) {
      return next.handle(req);
    }
    const tagged = req.clone({ setHeaders: { 'X-Batch-Id': batchId } });
    return next.handle(tagged);
  }
}
